<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KanbanAPI from 'dashboard/api/kanban';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const task = ref(null);
const steps = ref([]);
const boards = ref([]);
const selectedBoardId = ref(null);
const isLoading = ref(false);

const currentStepId = computed(() => task.value?.board_step_id);

const fetchTask = async () => {
  isLoading.value = true;
  try {
    const response = await KanbanAPI.getTasks({
      conversation_id: props.conversationId,
    });
    [task.value] = response.data.tasks;
    if (task.value) {
      const stepsResponse = await KanbanAPI.getSteps(task.value.board_id);
      steps.value = stepsResponse.data.steps;
    } else {
      const boardsResponse = await KanbanAPI.getBoards();
      boards.value = boardsResponse.data.boards;
      selectedBoardId.value = boards.value[0]?.id || null;
    }
  } catch (error) {
    // Falha silenciosa: painel é informativo, sem bloquear a conversa.
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchTask);
watch(() => props.conversationId, fetchTask);

const onStepChange = async event => {
  const stepId = Number(event.target.value);
  try {
    const response = await KanbanAPI.moveTask(task.value.id, stepId);
    task.value = response.data;
  } catch (error) {
    useAlert(error.message);
  }
};

const addToFunnel = async () => {
  if (!selectedBoardId.value) return;
  try {
    const stepsResponse = await KanbanAPI.getSteps(selectedBoardId.value);
    const firstStep = stepsResponse.data.steps[0];
    if (!firstStep) return;

    await KanbanAPI.createTask({
      title: `#${props.conversationId}`,
      board_id: selectedBoardId.value,
      board_step_id: firstStep.id,
      conversation_ids: [Number(props.conversationId)],
    });
    await fetchTask();
  } catch (error) {
    useAlert(error.message);
  }
};
</script>

<template>
  <div class="flex flex-col gap-2 px-4 py-2">
    <template v-if="task">
      <span class="text-xs text-n-slate-11">
        {{ t('KANBAN.CONVERSATION_SIDEBAR.STEP_LABEL') }}
      </span>
      <select
        :value="currentStepId"
        class="w-full px-2 py-1.5 text-sm border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
        @change="onStepChange"
      >
        <option v-for="step in steps" :key="step.id" :value="step.id">
          {{ step.name }}
        </option>
      </select>
    </template>
    <template v-else-if="!isLoading">
      <p class="text-xs text-n-slate-11">
        {{ t('KANBAN.CONVERSATION_SIDEBAR.NO_TASK') }}
      </p>
      <div v-if="boards.length" class="flex items-center gap-2">
        <select
          v-model="selectedBoardId"
          class="flex-1 min-w-0 px-2 py-1.5 text-sm border rounded-lg outline-none bg-n-solid-1 border-n-weak text-n-slate-12"
        >
          <option v-for="board in boards" :key="board.id" :value="board.id">
            {{ board.name }}
          </option>
        </select>
        <Button solid blue xs icon="i-lucide-plus" @click="addToFunnel">
          {{ t('KANBAN.CONVERSATION_SIDEBAR.CREATE_TASK') }}
        </Button>
      </div>
    </template>
  </div>
</template>
